import moment from 'moment';

import { FieldValidator } from '@/modules/dynamic-form/models/FieldValidator.model';
import { useDynamicFormStore } from '@/modules/dynamic-form/store';

import { interpretMessage } from '../../utils/interpret-message/interpretMessage';
import { CustomMessageOptions } from '../type';

const MESSAGE = 'Must be before {{beforeField}}';

/**
 * Creates a reactive validator that checks if a date is before another date.
 * The 'before' parameter can be either a static string or a getter function
 * that returns the latest value from the store, making it reactive to form changes.
 * this is DEPENDENT VALIDATION which means the input control needs to watch for dependent field value changes
 * and trigger validate when it happens
 *
 * @param before - A date string or a getter function that returns a date string
 * @param msgOptions - Options for custom messages and field names
 * @returns A validator function that returns true if valid, or an error message string if invalid
 *
 * @example
 * // Static value (not reactive)
 * createBeforeValidator('2025-12-31', { fieldName: 'Start Date' })
 *
 * @example
 * // Reactive value using a getter function
 * createBeforeValidator(
 *   () => useDynamicFormStore().forms[formId].values['policy.expirationDate'],
 *   { fieldName: 'Effective Date', beforeField: 'Expiration Date' }
 * )
 */
export function createBeforeValidator(
    before: string | (() => string),
    msgOptions?: CustomMessageOptions & { beforeField?: string },
): FieldValidator {
    const validate = (val: string) => {
        const momentVal = moment(val);
        // Resolve the 'before' value - if it's a function, call it to get the current value
        const beforeValue = typeof before === 'function' ? before() : before;
        const momentBefore = moment(beforeValue);

        if (!val || val === '' || !momentBefore.isValid()) {
            // if there is no value we should consider it valid as it should be part of required validator
            // if the before value is invalid, there is no constraint to check hence should be valid
            return true;
        }
        if (!momentVal.isValid()) {
            console.warn('value used in before validator is invalid', val);
            return 'Invalid date';
        }
        if (momentVal.isBefore(momentBefore)) {
            return true;
        }

        const message = msgOptions?.customMsg || MESSAGE;
        return interpretMessage(
            message,
            {
                field: msgOptions?.fieldName || 'field',
                beforeField: msgOptions?.beforeField || momentBefore.format('M/D/YYYY') || beforeValue,
            },
        );
    };
    return { name: 'date.before', validate };
}
